import { logger } from '@gik/analytics/utils/logger';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import type { AttachImageResult } from '@gik/core/utils/FileUtils';
import FileUtils, { AcceptType, DefaultMaxImageAttachmentSize } from '@gik/core/utils/FileUtils';
import noop from '@gik/core/utils/noop';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Button } from '@gik/ui/Button';
import type { IButtonProps } from '@gik/ui/Button/ButtonProps';
import { SvgIcon } from '@gik/ui/SvgIcon';
import PhotographIcon from '@heroicons/react/outline/PhotographIcon';
import React from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';

export interface IAttachmentPickerProps extends UIComponent {
  onUploadAttachment?: (attachment: AttachImageResult) => void;
  attachImageButtonVisible?: boolean;
  attachGIFButtonVisible?: boolean;
  attachImageButtonDisabled?: boolean;
  attachGIFButtonDisabled?: boolean;
  attachOnOpen?: boolean;
}

const blockName = 'attachment-picker';

function AttachmentPickerComp({
  onUploadAttachment = noop,
  attachImageButtonVisible,
  attachGIFButtonVisible,
  attachImageButtonDisabled = true,
  attachGIFButtonDisabled = false,
  className,
  attachOnOpen = false,
  ...otherProps
}: IAttachmentPickerProps) {
  const bem = useBemCN(blockName);

  useEffectOnce(() => {
    if (attachOnOpen) {
      onClickAttachImage();
    }
  });

  const defaultOptions: Partial<IButtonProps> = React.useMemo(
    () => ({
      circle: true,
      preventClickWhenDisabled: true,
      size: 'sm',
    }),
    []
  );

  const onClickAttachImage = React.useCallback(async () => {
    const attachImage = await FileUtils.attachImage(DefaultMaxImageAttachmentSize, [AcceptType.JPEG, AcceptType.PNG]);

    if (attachImage) {
      onUploadAttachment?.(attachImage);
    }
  }, [onUploadAttachment]);

  const onClickAttachGIF = React.useCallback(() => {
    logger.info('TODO');
  }, []);

  if (attachImageButtonVisible || attachGIFButtonVisible) {
    return (
      <div {...bem(null, null, className)} {...otherProps}>
        <label {...bem('add-attachment-label')}>Add:</label>
        {attachImageButtonVisible && (
          <Button onClick={onClickAttachImage} disabled={attachImageButtonDisabled} {...defaultOptions}>
            <SvgIcon Icon={PhotographIcon} />
          </Button>
        )}
        {attachGIFButtonVisible && (
          <Button
            variant={'secondary'}
            onClick={onClickAttachGIF}
            disabled={attachGIFButtonDisabled}
            {...defaultOptions}
          >
            GIF
          </Button>
        )}
      </div>
    );
  } else {
    return null;
  }
}

export const AttachmentPicker = withComponentErrorBoundary(AttachmentPickerComp);
